import React, { useEffect, useMemo, useRef, useState } from 'react'

import moment from 'moment'
import omit from 'omit-empty'
import translate from 'core/translate'
import { api } from 'core/api/ConnectionManager'
import helpers from 'core/helpers'
import helpersFiles, { commandsModel } from 'core/helpers/_files'
import { transitions } from 'blocks.app/config'
import { changeFilter } from 'features/routes'
import { RouteComponentProps } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { updateUserSettings } from 'blocks.app/user/user.state'
import type { IUserSettings } from 'core/models/Users'
import type { AppDispatch } from 'core/store'
import {
    CommandOptions,
    CommandParameters,
    ProcessedCommand,
    VideoConversionSettings,
    VideoEditorCommands,
    VideoEditorMedia,
    VideoEditorSettings,
    VideoEditorSidebarRef,
    VideoEditorTask,
    VideoEditorTaskData,
    VideoEditorUploadPayload,
} from './videoEditor.types'

const getDuration = (seconds: number) => moment().startOf('day').seconds(seconds).format('HH:mm:ss.SSS')
const animationKeys = ['fadeIn', 'fadeOut']

interface VideoEditorRootState {
    user: {
        data: {
            settings: IUserSettings
        }
    }
}

type CreateVideoEditorTaskRequest = Partial<{
    id: number
    sourceId: string | number
    sourceFolderId: number
}>

const getTaskSourceSettings = ({
    id,
    data,
    status,
    settings,
}: {
    id: number | null
    data: VideoEditorTaskData
    status: string
    settings: Pick<IUserSettings, 'videoConversion'>
}) => {
    const commands = getCommands(commandsModel as VideoEditorCommands, settings)

    const media: VideoEditorMedia = {
        src: data.fileURL || '',
        name: data.fileName || '',
        duration: data.duration,
        status,
        id,
        width: data.width,
        height: data.height,
        mimetype: data.mimetype,
        type: data.type,
        fps: data.fps,
        errors: data.videoValidationErrors || null,
        bitrate: data.bitrate,
        multiple: false,
    }

    commands.crop.height = media.height
    commands.crop.width = media.width

    if (media.type === 'video') {
        commands.cut.startTime = moment().startOf('day').format('HH:mm:ss')
        commands.cut.duration = helpers.msToHMS(data.duration * 1000, '')
    }

    commands.setVideoBitrate.bitrate = ''

    if (typeof data.bitrate === 'number') {
        commands.setVideoBitrate.bitrate = Math.round(data.bitrate / 1000)
    }

    if (data.hasOwnProperty('fps') && data.fps) {
        commands.setFps.fps = data.fps
    }

    return {
        media,
        commands,
    }
}
export interface IState {
    media: VideoEditorMedia | null
    downloadMediaPercentage: number | null
    showSettings: string | null
    minimized: boolean
    openAnimationSettings: boolean
    commands: VideoEditorCommands
    sourceData: VideoEditorCommands
    settings: VideoEditorSettings
    selectedTasks: VideoEditorTask[]
    selectedTasksSourceType: string | null
    usePreset: boolean
}
export interface IVideoEditorMethodsProps extends RouteComponentProps {
    minimized?: boolean
    media?: VideoEditorMedia | null
    commands?: VideoEditorCommands
    defaultCommands?: VideoEditorCommands
    onChangeCommands?: (commands: VideoEditorCommands) => void
    selectedTasksSourceType?: string | null
    toggleMinimized?: () => void
    reset?: () => void
    onDrop?: (content: { id: number; isDirectory: string }) => void
    onSelectTasks?: (tasks: VideoEditorTask[]) => void
    onUpload?: (file: File) => void
    onChangeTask?: (item: VideoEditorTask, type: string) => void
    onSelectTask?: () => void
    onProcessMedia?: () => void
    onChangeSettings?: (name: string, fileName: string) => void
    save?: () => void
    sourceId: string
    uploadVideoEditor: (payload: VideoEditorUploadPayload) => void
    emitError: (err: string) => void
}

const getCommands = (
    commands: VideoEditorCommands,
    settings: Pick<IUserSettings, 'videoConversion'>
): VideoEditorCommands => {
    let presetValue: Partial<VideoEditorCommands> | undefined

    switch (settings.videoConversion?.presetType) {
        case 0:
            const videoConversion = settings.videoConversion
            presetValue = {
                profile: {
                    profile: typeof videoConversion.profile === 'string' ? videoConversion.profile : '',
                },
                level: {
                    level:
                        typeof videoConversion.level === 'string' || typeof videoConversion.level === 'number'
                            ? videoConversion.level
                            : '',
                },
                videoCodec: {
                    videoCodec: typeof videoConversion.videoCodec === 'string' ? videoConversion.videoCodec : '',
                },
                audioCodec: {
                    audioCodec: typeof videoConversion.audioCodec === 'string' ? videoConversion.audioCodec : '',
                },
                preset: {
                    preset: typeof videoConversion.preset === 'string' ? videoConversion.preset : '',
                },
                crf: {
                    crf:
                        typeof videoConversion.crf === 'string' || typeof videoConversion.crf === 'number'
                            ? videoConversion.crf
                            : '',
                },
            }
            break
        case 1:
            presetValue = {
                profile: {
                    profile: 'baseline',
                },
                level: {
                    level: 3.0,
                },
                videoCodec: {
                    videoCodec: 'libx264',
                },
                audioCodec: {
                    audioCodec: 'aac',
                },
                crop: {
                    width: 1280,
                    height: 720,
                },
            }
            break
        case 2:
            presetValue = {
                profile: {
                    profile: 'baseline',
                },
                level: {
                    level: 3.0,
                },
                videoCodec: {
                    videoCodec: 'libx264',
                },
                removeAudio: {
                    active: true,
                },
                crop: {
                    width: 1280,
                    height: 720,
                },
                preset: {
                    preset: 'slow',
                },
                crf: {
                    crf: 23,
                },
            }
            break
    }

    return {
        ...commands,
        ...presetValue,
    } as VideoEditorCommands
}

const VideoEditorMethods = (props: IVideoEditorMethodsProps) => {
    const store = useSelector((store: VideoEditorRootState) => store)
    const memoizedUserSettings = useMemo(() => store.user.data.settings, [store.user.data.settings])
    const [state, setState] = useState<IState>({
        media: null,
        downloadMediaPercentage: null,
        showSettings: null,
        minimized: helpers.isMobileDevice(),
        openAnimationSettings: false,
        commands: getCommands(commandsModel as VideoEditorCommands, store.user.data.settings),
        sourceData: getCommands(commandsModel as VideoEditorCommands, store.user.data.settings),
        settings: {
            fileName: '',
            fileNamePrefix: translate('processed'),
            folderId: null,
        },
        selectedTasks: [],
        selectedTasksSourceType: null,
        usePreset:
            memoizedUserSettings.videoConversion?.presetType !== null &&
            memoizedUserSettings.videoConversion?.presetType !== undefined,
    })
    const initialPresetType = memoizedUserSettings.videoConversion?.presetType
    const [presetType, setPresetType] = useState<number | null>(
        typeof initialPresetType === 'number' ? initialPresetType : null
    )
    let minimizedTimeout: ReturnType<typeof setTimeout> | null = null
    const videoEditorSidebarRef = useRef<VideoEditorSidebarRef>(null)
    const dispatch = useDispatch<AppDispatch>()

    useEffect(() => {
        const { sourceId } = props
        sourceId && createVideoEditorTask({ sourceId })
        setState((prevState: IState) => {
            return {
                ...prevState,
                commands: getCommands(commandsModel as VideoEditorCommands, memoizedUserSettings),
                sourceData: getCommands(commandsModel as VideoEditorCommands, memoizedUserSettings),
            }
        })

        return () => {
            if (minimizedTimeout) {
                clearTimeout(minimizedTimeout)
            }
        }
    }, [])

    let setMediaModelCb: (() => void) | null = null
    const sourceData = useMemo(() => state.sourceData, [state.sourceData])
    useEffect(() => {
        if (JSON.stringify(sourceData) !== JSON.stringify(state.sourceData)) {
            setMediaModelCb && setMediaModelCb()
            setMediaModelCb = null
        }
    }, [state.sourceData])

    useEffect(() => {
        dispatch(updateUserSettings({ data: { videoConversion: { presetType } }, isSaveSettings: false }))
        const newSettings = {
            ...memoizedUserSettings,
            videoConversion: {
                ...memoizedUserSettings.videoConversion,
                presetType,
            },
        }

        setState((prevState: IState) => {
            return {
                ...prevState,
                commands: getCommands(prevState.commands, newSettings),
            }
        })
    }, [presetType])

    /**
     * [Toggle sidebar minimized state, close settings if sidebar was minimized]
     * @method toggleMinimized
     */
    const toggleMinimized = () => {
        const { minimized, showSettings } = state

        if (!minimized && showSettings) {
            if (minimizedTimeout) {
                clearTimeout(minimizedTimeout)
            }

            minimizedTimeout = setTimeout(() => {
                setState((prevState: IState) => {
                    return {
                        ...prevState,
                        showSettings: null,
                    }
                })
            }, transitions.normal)
        }

        setState((prevState: IState) => {
            return {
                ...prevState,
                minimized: !minimized,
            }
        })
    }

    /**
     * [Create video editor task for folder with images/videos and for single video/image file]
     * @method createVideoEditorTask
     * @param  {[object]}              data [only one key with id, sourceId or sourceFolderId]
     */
    const createVideoEditorTask = (data: CreateVideoEditorTaskRequest) => {
        api.send<CreateVideoEditorTaskRequest, VideoEditorTask | VideoEditorTask[]>(
            'createVideoEditorTaskFromSource',
            data
        ).then((videoEditorTaskData) => {
            clearFilter()

            if (Array.isArray(videoEditorTaskData)) {
                onSelectTasks(videoEditorTaskData)
                if (videoEditorSidebarRef.current?.setActiveSidebarTab) {
                    videoEditorSidebarRef.current?.setActiveSidebarTab('tasks')
                }

                return
            }

            setMediaModel(videoEditorTaskData)
        })
    }
    /**
     * [Clear app router filter on edit media source (sourceId)]
     * @method clearFilter
     */
    const clearFilter = () => {
        if (props.sourceId) {
            changeFilter({ sourceId: undefined })
        }
    }
    /**
     * [Reset data to default with clearFilter]
     * @method reset
     */
    const reset = () => {
        clearFilter()
        setState((prevState) => {
            return {
                ...prevState,
                media: null,
                showSettings: null,
                selectedTasksSourceType: null,
                selectedTasks: [],
            }
        })
    }
    /**
     * [Get dropped file from react-dropzone, execute createVideoEditorTask method]
     * @method onDrop
     * @param  {[object]} dropped [instance of file or folder, desctructed id and isDirectory fields]
     */
    const onDrop = ({ id, isDirectory }: { id: number; isDirectory: string }) => {
        const dataForCreateVideoEditorTask = {
            [isDirectory ? 'sourceFolderId' : 'sourceId']: id,
        }

        if (state.selectedTasksSourceType) {
            reset()
        }

        createVideoEditorTask(dataForCreateVideoEditorTask)
    }

    const saveContent = ({ id, isDirectory }: { id: number; isDirectory: string }) => {
        const dataForCreateVideoEditorTask = {
            [isDirectory ? 'sourceFolderId' : 'sourceId']: id,
        }

        if (state.selectedTasksSourceType) {
            reset()
        }

        createVideoEditorTask(dataForCreateVideoEditorTask)
    }

    /**
     * [Multiple select tasks with specific source type. If first task with source type video was selected, for tasks with image checkbox is disabled]
     * @method onSelectTasks
     * @param  {[array]}      tasks [Array of task instance]
     */
    const onSelectTasks = (tasks: VideoEditorTask[]) => {
        const { selectedTasks } = state
        const idx = 0
        const data: Partial<IState> = {
            selectedTasks: tasks,
        }

        if (tasks[idx]) {
            data.selectedTasksSourceType = tasks[idx].data.type

            data.selectedTasks = tasks.filter((task) => task.data.type === data.selectedTasksSourceType)
        }

        if (!selectedTasks.length && tasks.length) {
            data.selectedTasksSourceType = tasks[idx].data.type

            const { media, commands } = getTaskSourceSettings({
                data: {
                    fileURL: null,
                    fileName: null,
                    duration: 0,
                    width: 0,
                    height: 0,
                    type: data.selectedTasksSourceType,
                    fps: null,
                    mimetype: 'video/mp4',
                },
                status: 'success',
                id: null,
                settings: store.user.data.settings,
            })

            media.multiple = true
            data.media = media
            commands.convertToMp4.active = true
            data.commands = commands
        }

        if (!tasks.length) {
            data.selectedTasksSourceType = null
            data.media = null
        }

        setState((prevState: IState) => {
            return {
                ...prevState,
                ...data,
            }
        })
    }
    const setMediaModel = (task: VideoEditorTask, cb?: () => void) => {
        const settings = {
            fileName: `${translate('processed')}_${task.data.fileName}`,
            fileNamePrefix: translate('processed'),
            folderId: null,
        }
        const { media, commands } = getTaskSourceSettings({ ...task, settings: store.user.data.settings })
        if (cb) {
            setMediaModelCb = cb
        }

        setState((prevState: IState) => {
            return {
                ...prevState,
                media,
                settings,
                commands: commands,
                sourceData: commands,
            }
        })
    }
    const onUploadProgress = (percent: number | null) => {
        if (!percent) return
        setState((prevState: IState) => {
            return {
                ...prevState,
                downloadMediaPercentage: percent === 100 ? null : percent,
            }
        })
    }

    const onUploadComplete = (result: VideoEditorTask | null) => {
        const { media } = state

        if (!result || media) {
            setState((prevState: IState) => {
                return {
                    ...prevState,
                    downloadMediaPercentage: null,
                }
            })
            return
        }

        setMediaModel(result, () => {
            setState((prevState: IState) => {
                return {
                    ...prevState,
                    downloadMediaPercentage: null,
                    showSettings: 'edit',
                }
            })
        })
    }

    const onUpload = (file: File) => {
        const { uploadVideoEditor } = props

        uploadVideoEditor({
            file,
            onProgress: onUploadProgress,
            onComplete: onUploadComplete,
        })
    }
    const onChangeTask = (item: VideoEditorTask, type: string) => {
        const s_ = { ...state }

        if (!s_.media) {
            return
        }

        if (item.id === s_.media.id && type === 'delete') {
            setState((prevState: IState) => {
                return {
                    ...prevState,
                    media: null,
                }
            })
        }
    }

    const onSelectTask = (task: VideoEditorTask | null) => {
        const { selectedTasksSourceType } = state

        if (!task || selectedTasksSourceType) {
            return
        }

        setMediaModel(task, () => {
            if (helpers.isMobileDevice()) {
                toggleMinimized()
            }
        })
    }
    const onProcessMedia = () => {
        validate((data) => {
            const { media, selectedTasks } = state

            if (!media) {
                return
            }

            if (!data.setVideoBitrate && media.bitrate) {
                data.setVideoBitrate = { bitrate: Math.round(media.bitrate / 1000) }
            }

            const commands = formattingData(data)
            const processedData: { commands: ProcessedCommand[]; id: number | null | Array<number | null> } = {
                commands,
                id: media.id,
            }

            if (media.multiple) {
                processedData.id = selectedTasks.map((task) => task.id)
            }

            api.send<typeof processedData, void>('executeVideoEditorTask', processedData).then(() => {
                let updateStateData: Partial<IState> = {
                    showSettings: null,
                }

                if (media.multiple) {
                    updateStateData = {
                        ...updateStateData,
                        selectedTasks: [],
                        selectedTasksSourceType: null,
                        media: null,
                    }
                }
                setState((prevState: IState) => {
                    return {
                        ...prevState,
                        ...updateStateData,
                    }
                })
            })
        })
    }
    const onChangeSettings = (value: string | number | null, field: keyof VideoEditorSettings) => {
        setState((prevState) => ({
            ...prevState,
            settings: {
                ...prevState.settings,
                [field]: field === 'folderId' ? (typeof value === 'number' ? value : null) : String(value || ''),
            },
        }))
    }
    const formattingData = (data: CommandParameters): ProcessedCommand[] => {
        const commands: ProcessedCommand[] = []
        const subtitles = data.subtitles as VideoEditorCommands['subtitles'] | undefined

        if (subtitles && !subtitles.file) {
            delete data.subtitles
        }
        if (subtitles && subtitles.file) {
            delete subtitles.file[2]

            if (!subtitles.color) {
                subtitles.color = '#FFFFFF'
            }
            if (!subtitles.outlineColor) {
                subtitles.outlineColor = '#000000'
            }
            if (!subtitles.outlineType) {
                subtitles.outlineType = 'outline'
            }
            if (!subtitles.outlineSize) {
                subtitles.outlineSize = 3
            }
            if (!subtitles.backgroundColor) {
                subtitles.backgroundColor = '#000000'
            }
            if (!subtitles.fontId) {
                subtitles.fontId = 1
            }
            if (!subtitles.fontSize) {
                subtitles.fontSize = 26
            }
        }

        for (let key in data) {
            if (data.hasOwnProperty(key)) {
                const options = data[key]

                if (!options) {
                    continue
                }

                if (key === 'cut') {
                    const cut = options as VideoEditorCommands['cut']
                    if (cut.duration && cut.startTime) {
                        const durationSeconds = helpers.hmsToS(cut.duration)
                        const startTimeSeconds = helpers.hmsToS(cut.startTime)

                        cut.duration = getDuration(durationSeconds - startTimeSeconds)
                        cut.startTime = getDuration(startTimeSeconds)
                    }
                }

                if (data.cut && data.fadeOut) {
                    const cut = data.cut as VideoEditorCommands['cut']
                    const fadeOut = data.fadeOut
                    const sourceDuration = moment.duration(cut.duration).seconds()
                    fadeOut.startTime = sourceDuration - Number(fadeOut.duration || 0)
                }

                const model = {
                    action: key,
                    options,
                }

                if (options.hasOwnProperty('active')) {
                    model.options = {}
                }

                commands.push(model)
            }
        }

        return commands
    }
    const validate = (cb: (data: CommandParameters) => void) => {
        const s_ = { ...state }
        const { emitError } = props

        let parameters: CommandParameters = {}
        const commands = s_.commands
        const source = s_.sourceData

        if (s_.media && s_.media.type === 'image') {
            if (!commands.imageToVideo.duration) {
                emitError('enterDuration')
                return
            }
        }

        if (!commands.convertToMp4.active) {
            if (s_.media && !helpersFiles.availableVideoMimeTypes(s_.media.mimetype)) {
                commands.convertToMp4.active = true
            }
        }
        Object.entries(source).forEach(([sourceKey, sourceValue]) => {
            Object.entries(sourceValue).forEach(([key, value]) => {
                const currentCommand = commands[sourceKey][key]
                if (currentCommand !== value) {
                    parameters[sourceKey] = {
                        ...(parameters[sourceKey] || {}),
                        [key]: currentCommand,
                    }
                }

                if (sourceKey === 'crop') {
                    parameters[sourceKey] = {
                        ...(parameters[sourceKey] || {}),
                        [key]: currentCommand,
                    }
                }
                if (sourceKey === 'cut') {
                    parameters[sourceKey] = {
                        ...(parameters[sourceKey] || {}),
                        [key]: currentCommand,
                    }
                }
            })
        })

        parameters = omit(parameters)

        if (!Object.keys(parameters).length) {
            return false
        }

        if (validateTimeParameters(parameters.cut)) {
            if (validateCropParameters(parameters.crop)) {
                formattingAnimationParameters(parameters)
                const data = formattingBitrate(parameters)

                cb(data)
            }
        }
    }
    const formattingAnimationParameters = (parameters: CommandParameters) => {
        const s_ = { ...state }
        const duration =
            s_.media && s_.media.type === 'image' ? s_.commands?.imageToVideo.duration : s_.media && s_.media.duration

        for (let i = 0; animationKeys.length > i; i++) {
            const animation = animationKeys[i]
            const animationParameters = parameters[animation]

            if (animationParameters) {
                animationParameters.duration = Number(animationParameters.duration || 0) / 1000

                switch (animation) {
                    case 'fadeIn':
                        animationParameters.startTime = 0
                        break
                    default:
                        animationParameters.startTime =
                            Number(duration || 0) - Number(animationParameters.duration || 0)
                }
            }
        }
    }
    const validateTimeParameters = (parameters?: CommandOptions) => {
        const s_ = { ...state }
        const { emitError } = props

        if (!parameters) {
            return true
        }

        if (!parameters.startTime && parameters.duration) {
            parameters.startTime = moment().startOf('day').format('HH:mm:ss')
        }

        if (s_.media && s_.media.duration) {
            const duration = moment()
                .startOf('day')
                .seconds(s_.media && s_.media.duration)
                .format('HH:mm:ss')

            if (!parameters.duration && parameters.startTime) {
                parameters.duration = duration
            }

            const startTime = String(parameters.startTime || '')
            const parameterDuration = String(parameters.duration || '')

            if (parameterDuration < startTime) {
                emitError('endTimeCantBeLessStartTime')
                return false
            }

            if (s_.media && s_.media.multiple) {
                return true
            }

            if (startTime > duration) {
                emitError('startTimeCantBeMoreDuration')
                return false
            }

            if (parameterDuration > '00:10:00') {
                emitError('moreThan10Minutes')
                return false
            }
        }

        return true
    }
    const validateCropParameters = (parameters?: CommandOptions) => {
        const { emitError } = props
        let isValidParams = true

        if (parameters) {
            const validateParamsKeys = ['width', 'height']

            for (let key of validateParamsKeys) {
                if (!parameters[key]) {
                    emitError(`${key}Error`)

                    isValidParams = false
                }
            }

            if (isValidParams && state.media && state.media.type === 'image') {
                isValidParams = validateMaxCropImageSizes({
                    width: parameters.width,
                    height: parameters.height,
                })
            }
        }

        return isValidParams
    }
    /**
     * [Validate max crop values for image, check if sizes is full hd format]
     * @method validateMaxImageSizes
     * @param  {[number]}              width  [crop width]
     * @param  {[number]}              height [crop height]
     * @return {[boolean]}                    [returns valid/not valid]
     */
    const validateMaxCropImageSizes = ({
        width,
        height,
    }: {
        width: CommandOptions['width']
        height: CommandOptions['height']
    }) => {
        const { emitError } = props
        const IMAGE_MAX_SIZE = 1920

        if (parseInt(String(width || 0), 10) > IMAGE_MAX_SIZE) {
            emitError('imageWidthNoMoreThanFullHd')

            return false
        }

        if (parseInt(String(height || 0), 10) > IMAGE_MAX_SIZE) {
            emitError('imageHeightNoMoreThanFullHd')

            return false
        }

        return true
    }
    const validateSelectedFolder = (folderId: number | null) => {
        const { emitError } = props
        if (folderId) {
            return true
        }

        emitError('chooseFolder')
        setState((prevState: IState) => {
            return {
                ...prevState,
                showSettings: 'settings',
            }
        })

        return false
    }
    const formattingBitrate = (parameters: CommandParameters) => {
        if (parameters.hasOwnProperty('setVideoBitrate') && !parameters.setVideoBitrate.bitrate) {
            delete parameters.setVideoBitrate
        }

        return parameters
    }
    const save = () => {
        const { media, selectedTasks, settings } = state
        const data: {
            videoEditorTaskId: number | null | Array<number | null>
            sourceFolderId: number | null
            fileNamePrefix?: string
            fileName?: string
        } = {
            videoEditorTaskId: media ? media.id : null,
            sourceFolderId: settings.folderId,
        }

        if (!validateSelectedFolder(data.sourceFolderId)) {
            return
        }

        if (selectedTasks.length) {
            data.fileNamePrefix = settings.fileNamePrefix || translate('processed')
            data.videoEditorTaskId = selectedTasks.map((task) => task.id)
        } else {
            data.fileName = settings.fileName || `${translate('processed')}_${media?.name}`
        }

        api.send<typeof data, void>('saveSourceFromVideoEditorTask', data).then(() => {
            reset()
        })
    }

    return {
        state,
        setState,
        toggleMinimized,
        createVideoEditorTask,
        clearFilter,
        reset,
        onDrop,
        onSelectTasks,
        setMediaModel,
        onUploadProgress,
        onUploadComplete,
        onUpload,
        onChangeTask,
        onSelectTask,
        onProcessMedia,
        onChangeSettings,
        formattingData,
        validate,
        formattingAnimationParameters,
        validateTimeParameters,
        validateCropParameters,
        validateMaxCropImageSizes,
        validateSelectedFolder,
        formattingBitrate,
        save,
        videoEditorSidebarRef,
        saveContent,
        memoizedUserSettings,
        setPresetType,
        presetType,
    }
}

export default VideoEditorMethods
